import { useState } from 'react';

interface HelpPanelProps {
  step: string;
}

const helpContent: Record<string, { title: string; content: string[]; tips: string[] }> = {
  requirements: {
    title: 'Capture Requirements',
    content: [
      'Start by providing basic information about your project.',
      'Be as detailed as possible in the description - this helps the AI generate a better blueprint.',
      'List all key features you want in your application.',
      'Specify any technical or business constraints.',
    ],
    tips: [
      'Use clear, specific language',
      'Include target audience information',
      'Mention any existing systems to integrate with',
      'Specify performance or security requirements',
    ],
  },
  blueprint: {
    title: 'Review Blueprint',
    content: [
      'The AI has generated a comprehensive blueprint for your project.',
      'Review the architecture, tech stack, and coding standards.',
      'Once satisfied, freeze the blueprint to lock the specifications.',
      'Freezing prevents changes and enables manifest generation.',
    ],
    tips: [
      'Check that all your requirements are addressed',
      'Verify the tech stack matches your preferences',
      'Review coding standards and naming conventions',
      'Ensure the architecture pattern fits your needs',
    ],
  },
  manifest: {
    title: 'Build Manifest',
    content: [
      'The manifest contains concrete build instructions.',
      'It includes the complete file structure, dependencies, and CI/CD configuration.',
      'Review the files, dependencies, and policy checks.',
      'Use this to scaffold your GitHub repository.',
    ],
    tips: [
      'Verify all necessary files are included',
      'Check dependency versions are appropriate',
      'Review CI/CD checks for completeness',
      'Ensure policy rules match your standards',
    ],
  },
  jira: {
    title: 'Jira Plan',
    content: [
      'Generate a complete Jira plan with epics and stories.',
      'Each epic represents a major project phase.',
      'Stories include acceptance criteria and story points.',
      'This plan will be synchronized with your Jira project.',
    ],
    tips: [
      'Review story points for accuracy',
      'Check acceptance criteria are clear',
      'Ensure epics cover all features',
      'Verify task breakdowns are complete',
    ],
  },
  scaffold: {
    title: 'Scaffold Repository',
    content: [
      'Initialize your GitHub repository with the complete project structure.',
      'This creates all files, configuration, and CI/CD workflows.',
      'Branch protection rules are automatically applied.',
      'You\'ll need a GitHub Personal Access Token with repo and workflow scopes.',
    ],
    tips: [
      'Ensure the repository doesn\'t already exist',
      'Use a token with sufficient permissions',
      'Review what will be created before scaffolding',
      'Keep your token secure - never commit it',
    ],
  },
};

export function HelpPanel({ step }: HelpPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const help = helpContent[step] || helpContent.requirements;

  return (
    <>
      {/* Help Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 btn-primary rounded-full shadow-lg flex items-center justify-center z-40 transition-transform hover:scale-110"
        title="Help"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {/* Help Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {help.title}
                </h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Overview */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Overview
                </h3>
                <div className="space-y-2">
                  {help.content.map((paragraph, idx) => (
                    <p key={idx} className="text-gray-700 dark:text-gray-300">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              {/* Tips */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  💡 Tips
                </h3>
                <ul className="space-y-2">
                  {help.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Common Issues */}
              <div className="alert alert-warning">
                <h3 className="text-lg font-semibold text-warning mb-2">
                  ⚠️ Common Issues
                </h3>
                <ul className="space-y-1 text-sm text-warning">
                  <li>• If generation fails, check your AI provider API keys</li>
                  <li>• Ensure you have a stable internet connection</li>
                  <li>• For GitHub scaffolding, verify token permissions</li>
                  <li>• Contact support if issues persist</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-background-secondary border-t border-border p-6">
              <button
                onClick={() => setIsOpen(false)}
                className="btn btn-primary w-full"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
